import { useCallback } from 'react';

import { useSessionSaveMutation } from '@/api/hooks/mutations/use-session-save-mutation';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';

/**
 * Hook to automatically save user session data.
 *
 * Usage:
 * ```tsx
 * const { saveSession } = useSessionSave();
 *
 * const onSubmit = (data) => {
 *   updateStore(data);
 *   saveSession(); // Fire and forget - non-blocking
 *   navigate({ to: '/next-step' });
 * };
 * ```
 */
export function useSessionSave() {
  const { mutate } = useSessionSaveMutation();

  const saveSession = useCallback(() => {
    const state = useSessionStore.getState();

    // Only save if we have an email to associate with the session and a DOB
    if (!state.contactEmail || !state.patientDateOfBirth) {
      return;
    }

    // Fire and forget
    mutate({
      email: state.contactEmail,
      dob: state.patientDateOfBirth,
      data: { ...state },
    });
  }, [mutate]);

  return {
    saveSession,
  };
}
